import React, { useState, useEffect } from 'react';

// libraries
import { connect, DispatchProp, InferableComponentEnhancerWithProps, useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { useTranslation } from 'react-i18next';

// ui components
import { Container } from './Auth.styles';

// utilities
import authenticateCredentials from '../utils/authenticateCredentials';
import authenticatePasswordReset from '../utils/authenticatePasswordReset';
import {
	authenticateFederatedCredentials,
	checkUserEnrollment,
	requestAccessEnrollment,
	authenticateMultiTenant,
	linkAccount,
} from 'services/federatedLogin.service';
import { setEnrollmentStatus, setAuthenticatingStatus } from 'reducers/userEnrollment/userEnrollment.action';

// components
import AuthContainer from '../Auth/AuthContainer';

// types
import { RootState } from 'store/rootReducers';
import { REJECTED_SESSION_STORAGE_KEY, rejectedSessionMessage } from 'constants/errorKeys';
import { FederatedAuthentication } from '@nimbly-technologies/nimbly-common';
import postCheckIsSandbox from '../utils/authenticateIsSandboxApi';
import { toast } from 'helpers/toast';

// Sentry tracking
import {
	trackUserAction,
	trackOperation,
	logErrorWithContext,
	generateSessionId,
	trackSessionStart,
	trackSessionEnd,
	identifyUser,
	clearUser,
} from 'utils/sentry/sentrySetup';

enum EnrollmentStatus {
	SUCCESS = 'SUCCESS',
	NOT_APPROVED = 'not-approved',
}

export interface SitePropsLoginData {
	/** (String) user email address, retrieved from form */
	email: string;
	/** (String) user input password, retrieved from form */
	password: string;
}

export interface SitePropsForgotPasswordData {
	/** (String) user email address, retrieved from forgot password form */
	email: string;
}

export interface SitePropsErrorFormFields {
	/** If true then email doesn't pass system validation */
	email: boolean;
	/** If true then password doesn't pass system validation */
	password: boolean;
	/** If true then email (in forgot password form) doesn't pass system validation */
	forgotPass: boolean;
}

export interface SitePropsFlashMessage {
	/** Error message that will be shown */
	errorMessage: string;
	/** Success message that will be shown */
	successMessage: string;
	/** Referes to SitePropsErrorFormFields, defining wether each form field is valid / not */
	hasError: SitePropsErrorFormFields;
}
interface TenantTypes {
	federatedAuthentication: FederatedAuthentication | null;
	isFederated: boolean;
	isOrganizationTenant: false;
}

export const Auth = (props: AuthProps) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const firebase = useFirebase();

	const [loginCredentials, setLoginCredentials] = useState<SitePropsLoginData>({
		email: '',
		password: '',
	});
	const [forgotPasswordData, setForgotPasswordData] = useState<SitePropsForgotPasswordData>({
		email: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [modeForgot, setModeForgot] = useState(false);
	const [siteFlashMessage, setSiteFlashMessage] = useState<SitePropsFlashMessage>({
		errorMessage: '',
		successMessage: '',
		hasError: {
			email: false,
			password: false,
			forgotPass: false,
		},
	});
	const [tenant, setTenant] = useState<TenantTypes>({
		isFederated: false,
		isOrganizationTenant: false,
		federatedAuthentication: null,
	});
	const [validationMessage, setValidationMessage] = useState<string>('');
	const [isRequestAccess, setIsRequestAccess] = useState<boolean>(false);
	const [isRequestSuccess, setIsRequestSuccess] = useState<boolean>(false);
	const [isRequestFailed, setIsRequestFailed] = useState<boolean>(false);
	const [isMultipleRequest, setIsMultipleRequest] = useState<boolean>(false);
	const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
	const [idToken, setIdToken] = useState<string>('');
	/** Required to handle if the account is not yet linking */
	const [pendingCredential, setPendingCredential] = useState<any>(null);

	const setStatusMultipleRequest = () => {
		setIsLoading(false);
		setIsRequestAccess(true);
		setIsRequestFailed(true);
		setIsMultipleRequest(true);
	};

	const handleAccountExist = (pendingCred: any) => {
		setIsLoading(false);
		setPendingCredential(pendingCred);
		setTenant({ ...tenant, isFederated: false });
		setValidationMessage('Password is required to link the account');
	};

	const handleFailedResult = (result: any) => {
		setIsLoading(false);
		setSiteFlashMessage({
			...siteFlashMessage,
			errorMessage: result.errorMsg,
			hasError: {
				...siteFlashMessage.hasError,
				[result.errorField]: result.status !== 'field_not_complete',
			},
		});
	};

	const loginAsFederated = async () => {
		const authSessionId = generateSessionId();
		trackSessionStart('federated_auth', authSessionId, {
			tenant_id: tenant?.federatedAuthentication?.tenantID,
		});

		trackUserAction('federated_auth_attempt', {
			tenant_id: tenant?.federatedAuthentication?.tenantID,
		});

		const getIDToken = await authenticateFederatedCredentials(
			tenant.federatedAuthentication as FederatedAuthentication,
			firebase,
		);

		/** When pop up cancelled/closed */
		if (getIDToken?.errorCode === 'auth/user-cancelled' || getIDToken?.errorCode === 'auth/popup-closed-by-user') {
			trackOperation('federated_auth', 'failure', {
				session_id: authSessionId,
				reason: 'popup_closed',
				error_code: getIDToken?.errorCode,
			});
			trackSessionEnd('federated_auth', authSessionId, 'failure', { reason: 'popup_closed' });
			return window.location.reload();
		}

		/** Handle certain user get account-exist-with-different-creds */
		if (getIDToken?.errorCode === 'auth/account-exists-with-different-credential') {
			trackOperation('federated_auth', 'failure', {
				session_id: authSessionId,
				reason: 'account_exists_different_credential',
			});
			trackSessionEnd('federated_auth', authSessionId, 'failure', {
				reason: 'account_exists_different_credential',
			});
			return handleAccountExist(getIDToken?.pendingCred);
		}

		setIdToken(getIDToken);
		const getUserEnrollmentStatus = await checkUserEnrollment(
			getIDToken,
			String(tenant?.federatedAuthentication?.tenantID),
		);

		// User Enrollment is not yet approved
		if (!getUserEnrollmentStatus.data && getUserEnrollmentStatus.message === EnrollmentStatus.NOT_APPROVED) {
			trackOperation('federated_auth', 'failure', {
				session_id: authSessionId,
				reason: 'enrollment_not_approved',
			});
			trackSessionEnd('federated_auth', authSessionId, 'failure', { reason: 'enrollment_not_approved' });
			return setStatusMultipleRequest();
		}

		if (!getUserEnrollmentStatus.data) {
			trackOperation('federated_auth', 'failure', {
				session_id: authSessionId,
				reason: 'no_permission',
			});
			trackSessionEnd('federated_auth', authSessionId, 'failure', { reason: 'no_permission' });
			setValidationMessage(t('message.loginScreen.noPermission'));
			setIsRequestAccess(true);
		} else {
			const status = getUserEnrollmentStatus.message === EnrollmentStatus.SUCCESS ? EnrollmentStatus.SUCCESS : null;
			dispatch(setEnrollmentStatus(status));

			// Identify user in Sentry on successful federated login
			identifyUser({
				email: getUserEnrollmentStatus.data?.email,
				id: getUserEnrollmentStatus.data?.uid,
				displayName: getUserEnrollmentStatus.data?.displayName,
			});

			trackOperation('federated_auth', 'success', {
				session_id: authSessionId,
			});
			trackSessionEnd('federated_auth', authSessionId, 'success');
			setSiteFlashMessage({
				...siteFlashMessage,
				successMessage: t('message.loginScreen.successLogin'),
			});
		}
		setIsAuthenticating(false);
		dispatch(setAuthenticatingStatus(false));
		setIsLoading(false);
	};

	const loginAsMultiTenant = async () => {
		const authSessionId = generateSessionId();
		trackSessionStart('multi_tenant_auth', authSessionId);
		trackUserAction('multi_tenant_login_attempt');

		const validateLogin = await authenticateMultiTenant(
			tenant.federatedAuthentication as FederatedAuthentication,
			loginCredentials.email,
			loginCredentials.password,
		);

		if (validateLogin.status === 'ok') {
			dispatch(setEnrollmentStatus(EnrollmentStatus.SUCCESS));

			// Identify user in Sentry on successful multi-tenant login
			identifyUser({
				email: loginCredentials.email,
			});

			trackOperation('multi_tenant_auth', 'success', { session_id: authSessionId });
			trackSessionEnd('multi_tenant_auth', authSessionId, 'success');
			// Keep isLoading=true on success - redirect will happen via auth state change
			// Safety timeout: reset loading if redirect does not happen within 10 seconds
			setTimeout(() => setIsLoading(false), 10000);
			return;
		}

		trackOperation('multi_tenant_auth', 'failure', {
			session_id: authSessionId,
			error_field: validateLogin.errorField,
		});
		trackSessionEnd('multi_tenant_auth', authSessionId, 'failure');
		return handleFailedResult(validateLogin);
	};

	const loginAsNonFederated = async () => {
		const authSessionId = generateSessionId();
		trackSessionStart('standard_auth', authSessionId);
		trackUserAction('login_attempt', { is_federated: false });

		const checkLoginFirebase = await authenticateCredentials(
			loginCredentials.email,
			loginCredentials.password,
			firebase,
		);

		if (checkLoginFirebase.status === 'ok') {
			dispatch(setEnrollmentStatus(EnrollmentStatus.SUCCESS));

			// Identify user in Sentry on successful standard login
			identifyUser({
				email: loginCredentials.email,
			});

			trackOperation('user_login', 'success', { session_id: authSessionId });
			trackSessionEnd('standard_auth', authSessionId, 'success');
			// Keep isLoading=true on success - redirect will happen via auth state change
			// Safety timeout: reset loading if redirect does not happen within 10 seconds
			setTimeout(() => setIsLoading(false), 10000);
			return;
		}

		trackOperation('user_login', 'failure', {
			session_id: authSessionId,
			error_field: checkLoginFirebase.errorField,
			status: checkLoginFirebase.status,
		});
		trackSessionEnd('standard_auth', authSessionId, 'failure', {
			error_field: checkLoginFirebase.errorField,
		});
		return handleFailedResult(checkLoginFirebase);
	};

	const linkingAccount = async () => {
		const authSessionId = generateSessionId();
		trackSessionStart('account_linking', authSessionId);
		trackUserAction('account_link_attempt');

		const checkLinkAccount = await linkAccount(loginCredentials.email, loginCredentials.password, pendingCredential);

		if (checkLinkAccount.status === 'ok') {
			dispatch(setEnrollmentStatus(EnrollmentStatus.SUCCESS));

			// Identify user in Sentry on successful account linking
			identifyUser({
				email: loginCredentials.email,
			});

			trackOperation('account_linking', 'success', { session_id: authSessionId });
			trackSessionEnd('account_linking', authSessionId, 'success');
			// Keep isLoading=true on success - redirect will happen via auth state change
			// Safety timeout: reset loading if redirect does not happen within 10 seconds
			setTimeout(() => setIsLoading(false), 10000);
			return;
		}

		trackOperation('account_linking', 'failure', {
			session_id: authSessionId,
			error_field: checkLinkAccount.errorField,
		});
		trackSessionEnd('account_linking', authSessionId, 'failure');
		return handleFailedResult(checkLinkAccount);
	};

	const forgotPassword = async () => {
		trackUserAction('forgot_password_attempt');

		const reset_password = await authenticatePasswordReset(forgotPasswordData.email);
		setIsLoading(false);

		if (reset_password.status === 'ok') {
			setForgotPasswordData({ email: '' });
			trackOperation('password_reset', 'success');
			toast.success('Password reset link sent successfully.');
			setSiteFlashMessage({
				...siteFlashMessage,
				successMessage: t('message.loginScreen.passwordRequest'),
			});
		} else {
			trackOperation('password_reset', 'failure', {
				error_field: reset_password.errorField,
			});
		}

		setSiteFlashMessage({
			...siteFlashMessage,
			// `errorMsg` is null on success and SitePropsFlashMessage wants a
			// string. The helper's old inferred `Promise<any>` hid that; its
			// explicit return type does not.
			errorMessage: reset_password.errorMsg ?? '',
			hasError: {
				...siteFlashMessage.hasError,
				// Only when there is a field to flag. This used to key on the
				// value unconditionally, so a result carrying no field wrote a
				// literal "null" entry that nothing ever read.
				...(reset_password.errorField
					? { [reset_password.errorField]: reset_password.status !== 'field_not_complete' }
					: {}),
			},
		});
	};

	const handleSubmitForm = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>,
	) => {
		e.preventDefault();
		setIsLoading(true);
		setSiteFlashMessage({ ...siteFlashMessage, successMessage: '', errorMessage: '' });

		trackUserAction('auth_form_submit', {
			is_federated: tenant.isFederated,
			is_multi_tenant: tenant.isOrganizationTenant,
			is_forgot_password: modeForgot,
			has_pending_credential: !!pendingCredential,
		});

		if (tenant.federatedAuthentication && !tenant.isOrganizationTenant && tenant.isFederated) {
			dispatch(setAuthenticatingStatus(true));
			setIsAuthenticating(true);
			return loginAsFederated();
		}
		if (tenant.isOrganizationTenant) return loginAsMultiTenant();
		if (modeForgot) return forgotPassword();
		if (pendingCredential) return linkingAccount();

		await postCheckIsSandbox(loginCredentials.email);
		await loginAsNonFederated();
	};

	const handleDismissRequestSuccess = () => {
		trackUserAction('dismiss_request_success');
		// Clear Sentry user on logout
		clearUser();
		firebase.logout().then(() => {
			window.location.reload();
		});
	};

	const handleRequestAccess = async () => {
		setIsLoading(true);
		trackUserAction('request_access_attempt');

		try {
			setValidationMessage('');
			const result = await requestAccessEnrollment(idToken, tenant.federatedAuthentication as FederatedAuthentication);
			if (!result) {
				trackOperation('request_access', 'failure');
				setIsRequestFailed(true);
			} else {
				trackOperation('request_access', 'success');
			}
			setIsRequestSuccess(true);
		} catch (error) {
			logErrorWithContext(error, 'auth_request_access_error', {
				tenant_id: tenant?.federatedAuthentication?.tenantID,
			});
			console.error(error);
		}
		setIsLoading(false);
	};

	const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValidationMessage('');
		setSiteFlashMessage({ ...siteFlashMessage, successMessage: '', errorMessage: '' });
		setLoginCredentials({ ...loginCredentials, email: e.target.value });
	};

	const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
		setValidationMessage('');
		if (e.target.value === '') {
			setSiteFlashMessage({
				...siteFlashMessage,
				hasError: { ...siteFlashMessage.hasError, password: false },
			});
		}

		setLoginCredentials({ ...loginCredentials, password: e.target.value });
	};

	const handleToggleForgotPass = () => {
		trackUserAction('toggle_forgot_password', { to_forgot_mode: !modeForgot });
		setModeForgot(!modeForgot);
		setSiteFlashMessage({
			...siteFlashMessage,
			successMessage: '',
			errorMessage: '',
		});
	};

	/** Check for email address format on real time (when text field updated), for forgot password email field */
	const handleChangeForgotPass = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSiteFlashMessage({
			...siteFlashMessage,
			hasError: { ...siteFlashMessage.hasError, forgotPass: false },
		});

		setForgotPasswordData({ email: e.target.value });
	};

	// Watch for disabled user error flag from App.tsx
	useEffect(() => {
		const disabledUserErrorKey = localStorage.getItem(REJECTED_SESSION_STORAGE_KEY);
		if (disabledUserErrorKey) {
			const errorMsg = t(rejectedSessionMessage(disabledUserErrorKey));

			// Track the disabled user error
			logErrorWithContext(new Error(errorMsg), 'auth_disabled_user_error', {
				error_key: disabledUserErrorKey,
			});

			// Show error message
			const errorResult = {
				status: 'user_disabled',
				errorMsg,
				errorField: '',
				data: null,
			};

			handleFailedResult(errorResult);
			localStorage.removeItem(REJECTED_SESSION_STORAGE_KEY);
		}
	}, [props.auth?.uid, t]);

	return (
		<Container mode={{ modeForgot }}>
			<AuthContainer
				// Disabled for now, no organization used federated login
				isFederated={false}
				isRequestAccess={isRequestAccess}
				isRequestSuccess={isRequestSuccess}
				isRequestFailed={isRequestFailed}
				isAuthenticating={isAuthenticating}
				isMultipleRequest={isMultipleRequest}
				loginCredentials={loginCredentials}
				forgotPasswordData={forgotPasswordData}
				isLoading={isLoading}
				modeForgot={modeForgot}
				siteFlashMessage={siteFlashMessage}
				validationMessage={validationMessage}
				handleSubmitForm={handleSubmitForm}
				handleChangeEmail={handleChangeEmail}
				handleChangePassword={handleChangePassword}
				handleChangeForgotPass={handleChangeForgotPass}
				handleToggleForgotPass={handleToggleForgotPass}
				handleRequestAccess={handleRequestAccess}
				handleDismissRequestSuccess={handleDismissRequestSuccess}
			/>
		</Container>
	);
};

const mapStateToProps = (state: RootState) => ({
	auth: state.firebase.auth,
	location: state.router.location,
});

/** DO NOT CHANGE - Make props out of mapStateToProps function */
type StateProps = ReturnType<typeof mapStateToProps>;

/** Put any custom props used by other library such as redux or firebase */
type EnhancedProps = StateProps & DispatchProp;

/** Put custom props here */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface OwnProps {}

export type AuthProps = OwnProps & EnhancedProps;

const enhance: InferableComponentEnhancerWithProps<EnhancedProps, OwnProps> = connect(mapStateToProps);

export default enhance(Auth);
